import { useState, useRef, useCallback } from 'react';

const LogoBar = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || !containerRef.current) return;
    const x = e.touches[0].pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const logos = [
    { src: "/lovable-uploads/eagle-eye-logo.png", alt: "Eagle Eye Investments", needsBackground: false },
    { src: "/lovable-uploads/diamond-point-logo.png", alt: "Diamond Point Home Buyers", needsBackground: false },
    { src: "/lovable-uploads/columbus-home-buyers-logo.png", alt: "Columbus Home Buyers", needsBackground: false },
    { src: "/lovable-uploads/viral-fox-logo.png", alt: "Peregrine REI", needsBackground: false },
    { src: "/lovable-uploads/quick-direct-sale-logo.png", alt: "Quick Direct Sale", needsBackground: true },
    { src: "/lovable-uploads/right-price-logo.png", alt: "Right Price Home Buyers", needsBackground: false },
  ];

  const LogoItem = ({ logo }: { logo: typeof logos[0] }) => (
    <div 
      className="flex items-center justify-center grayscale opacity-50 hover:grayscale-0 hover:opacity-100 hover:scale-110 transition-all duration-300 ease-out flex-shrink-0 mx-8 select-none"
    >
      {logo.needsBackground ? (
        <div className="w-24 h-24 rounded-full bg-background flex items-center justify-center p-3 shadow-sm border border-border">
          <img 
            src={logo.src} 
            alt={logo.alt} 
            width={72}
            height={72}
            loading="lazy"
            draggable={false}
            className="w-full h-full object-contain pointer-events-none"
          />
        </div>
      ) : (
        <div className="w-24 h-24 rounded-full bg-muted/50 dark:bg-transparent flex items-center justify-center p-3">
          <img 
            src={logo.src} 
            alt={logo.alt} 
            width={72}
            height={72}
            loading="lazy"
            draggable={false}
            className="w-full h-full object-contain pointer-events-none"
          />
        </div>
      )}
    </div>
  );

  const shouldAnimate = !isHovered && !isDragging;

  return (
    <div className="w-full py-8 overflow-hidden">
      <p className="text-center text-xs text-muted-foreground uppercase tracking-widest mb-6 px-6">
        Powering acquisition for elite wholesalers
      </p>
      <div 
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsDragging(false);
        }}
      >
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        
        {/* Scrolling container */}
        <div 
          ref={containerRef}
          className={`flex overflow-x-auto scrollbar-hide ${shouldAnimate ? 'animate-marquee' : ''} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* First set of logos */}
          {logos.map((logo) => (
            <LogoItem key={logo.alt} logo={logo} />
          ))}
          {/* Duplicate set for seamless loop */}
          {logos.map((logo) => (
            <LogoItem key={`${logo.alt}-duplicate`} logo={logo} />
          ))}
          {/* Third set for extra smoothness */}
          {logos.map((logo) => (
            <LogoItem key={`${logo.alt}-triplicate`} logo={logo} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LogoBar;
